import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, RefreshCw, Play, ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

// Admin → Jobs (Jobs Engine Part 1, 09/22/2026). Reads /api/admin/jobs; every job
// is report/notify only — nothing here mutates deals, transactions or syndications.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOBS = `${API}/admin/jobs`;

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtDur = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const fmtCounts = (c) => (c && Object.keys(c).length ? Object.entries(c).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ') : '—');

export default function AdminJobs() {
  const { getAuthHeaders } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [runs, setRuns] = useState({});
  const [paramDraft, setParamDraft] = useState({});

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(JOBS, { headers: getAuthHeaders() });
      setJobs(res.data?.jobs || []);
    } catch (e) {
      toast.error(`Failed to load jobs: ${e.response?.data?.detail || e.message}`);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // Poll while any job is running so "Run now" outcomes show up without a manual refresh.
  useEffect(() => {
    if (!jobs.some((j) => j.running)) return undefined;
    const t = setInterval(() => load(true), 3000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const loadRuns = async (name) => {
    try {
      const res = await axios.get(`${JOBS}/${name}/runs?limit=25`, { headers: getAuthHeaders() });
      setRuns((r) => ({ ...r, [name]: res.data?.runs || [] }));
    } catch (e) {
      toast.error(`Failed to load runs: ${e.response?.data?.detail || e.message}`);
    }
  };

  const toggleExpand = async (j) => {
    if (expanded === j.job) { setExpanded(null); return; }
    setExpanded(j.job);
    setParamDraft((d) => ({ ...d, [j.job]: { ...(j.params || {}) } }));
    await loadRuns(j.job);
  };

  const runNow = async (name) => {
    setBusy(name);
    try {
      await axios.post(`${JOBS}/${name}/run-now`, {}, { headers: getAuthHeaders() });
      toast.success(`${name} started`);
      await load(true);
      setTimeout(() => { load(true); if (expanded === name) loadRuns(name); }, 2500);
    } catch (e) {
      toast.error(`Run failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const setEnabled = async (name, enabled) => {
    setBusy(name);
    try {
      await axios.patch(`${JOBS}/${name}`, { enabled }, { headers: getAuthHeaders() });
      setJobs((js) => js.map((j) => (j.job === name ? { ...j, enabled } : j)));
      toast.success(`${name} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const saveParams = async (name) => {
    setBusy(name);
    try {
      const res = await axios.patch(`${JOBS}/${name}`, { params: paramDraft[name] || {} }, { headers: getAuthHeaders() });
      setJobs((js) => js.map((j) => (j.job === name ? { ...j, params: res.data.params } : j)));
      setParamDraft((d) => ({ ...d, [name]: { ...res.data.params } }));
      toast.success(`${name} params saved`);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const StatusBadge = ({ run, running, stub }) => {
    if (stub) return <Badge variant="secondary">not built</Badge>;
    if (running) return <Badge className="bg-blue-600"><Loader2 className="w-3 h-3 mr-1 animate-spin" />running</Badge>;
    if (!run) return <Badge variant="outline">never run</Badge>;
    if (run.ok) return <Badge className="bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" />ok</Badge>;
    if (run.status === 'skipped_disabled') return <Badge variant="secondary">skipped</Badge>;
    return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />error</Badge>;
  };

  return (
    <Layout>
      <div className="p-6 w-full max-w-[1700px] mx-auto">  {/* 10/08 (Josh: "make the dialogue larger") — was max-w-6xl; the 7-column table clipped the Job column */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Jobs</h1>
            <p className="text-sm text-muted-foreground">Scheduled jobs — each run is recorded; jobs report and notify only. Times shown in New York.</p>
          </div>
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <Card className="overflow-x-auto">
            <CardContent className="p-0 min-w-[1100px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Job</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Last run</TableHead>
                    <TableHead>Counts</TableHead>
                    <TableHead>Next</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((j) => {
                    const lr = j.last_run;
                    const open = expanded === j.job;
                    return (
                      <React.Fragment key={j.job}>
                        <TableRow className="cursor-pointer" onClick={() => toggleExpand(j)} data-testid={`job-row-${j.job}`}>
                          <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                          <TableCell>
                            <div className="font-mono text-sm font-medium">{j.job}{j.legacy ? <Badge variant="outline" className="ml-2 text-[10px]">existing</Badge> : null}</div>
                            <div className="text-xs text-muted-foreground max-w-xl">{j.description}</div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{j.schedule || '—'}</TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Switch checked={!!j.enabled} disabled={j.stub || busy === j.job} onCheckedChange={(v) => setEnabled(j.job, v)} />
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            <StatusBadge run={lr} running={j.running} stub={j.stub} />
                            {lr ? <div className="text-xs text-muted-foreground mt-1">{fmtWhen(lr.started_at)} · {fmtDur(lr.duration_ms)} · {lr.triggered_by}</div> : null}
                            {lr?.error ? <div className="text-xs text-red-500 max-w-xs truncate" title={lr.error}>{lr.error}</div> : null}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-xs">{fmtCounts(lr?.counts)}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap"><Clock className="w-3 h-3 inline mr-1" />{j.enabled ? fmtWhen(j.next_run_at) : 'paused'}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" disabled={j.stub || j.running || busy === j.job} onClick={() => runNow(j.job)}>
                              {busy === j.job ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Run now
                            </Button>
                          </TableCell>
                        </TableRow>
                        {open && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-muted/30">
                              <div className="grid md:grid-cols-2 gap-6 p-2">
                                <div>
                                  <div className="font-semibold text-sm mb-2">Parameters</div>
                                  {Object.keys(j.default_params || {}).length === 0 ? (
                                    <p className="text-xs text-muted-foreground">This job has no parameters.</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {Object.entries(j.default_params).map(([k, dv]) => (
                                        <label key={k} className="flex items-center gap-3 text-sm">
                                          <span className="w-44 font-mono text-xs">{k}</span>
                                          {typeof dv === 'boolean' ? (
                                            <Switch checked={!!(paramDraft[j.job]?.[k])} onCheckedChange={(v) => setParamDraft((d) => ({ ...d, [j.job]: { ...(d[j.job] || {}), [k]: v } }))} />
                                          ) : (
                                            <Input className="h-8 w-40" value={paramDraft[j.job]?.[k] ?? ''} type={typeof dv === 'number' ? 'number' : 'text'} step="any"
                                              onChange={(e) => setParamDraft((d) => ({ ...d, [j.job]: { ...(d[j.job] || {}), [k]: e.target.value } }))} />
                                          )}
                                          <span className="text-xs text-muted-foreground">{j.param_help?.[k] || `default ${String(dv)}`}</span>
                                        </label>
                                      ))}
                                      <Button size="sm" onClick={() => saveParams(j.job)} disabled={busy === j.job}>Save params</Button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-semibold text-sm mb-2">Run history</div>
                                  {(runs[j.job] || []).length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No runs recorded yet.</p>
                                  ) : (
                                    <div className="max-h-72 overflow-y-auto text-xs">
                                      <table className="w-full">
                                        <tbody>
                                          {runs[j.job].map((r) => (
                                            <tr key={r.id} className="border-b border-border/50 align-top">
                                              <td className="py-1 pr-2 whitespace-nowrap">{fmtWhen(r.started_at)}</td>
                                              <td className="py-1 pr-2">{r.ok ? <span className="text-emerald-500">ok</span> : r.status === 'running' ? <span className="text-blue-500">running</span> : <span className="text-red-500">{r.status}</span>}</td>
                                              <td className="py-1 pr-2 whitespace-nowrap">{fmtDur(r.duration_ms)}</td>
                                              <td className="py-1 pr-2">{r.triggered_by}{r.triggered_by_user ? ` (${r.triggered_by_user})` : ''}</td>
                                              <td className="py-1 text-muted-foreground">{r.error ? <span className="text-red-500">{r.error}</span> : fmtCounts(r.counts)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
